import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { BrowserRouter, Navigate, Routes, Route, useLocation, useParams } from "react-router-dom";
import { AuthProvider } from "./contexts/AuthContext";
import { SubscriptionProvider } from "./contexts/SubscriptionContext";
import { ProtectedRoute } from "./components/shared/ProtectedRoute";
import { RequireOnboarding } from "./components/shared/RequireOnboarding";
import { RedirectOnboardedUser } from "./components/shared/RedirectOnboardedUser";
import { BillingModalProvider } from "@/features/billing";
import { lazy, Suspense, useEffect } from "react";
import { applyTheme, getPreferredTheme, normalizeStoredTheme } from "./hooks/use-theme";
import { APP_PAPERS_ROUTE, paperRoute } from "@/lib/routes";

const PublishedPaper = lazy(() => import("./pages/PublishedPaper"));
const NotFound = lazy(() => import("./pages/NotFound"));
const Landing = lazy(() => import("./pages/Landing"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const TermsOfService = lazy(() => import("./pages/TermsOfService"));
const Paper = lazy(() => import("./pages/Paper"));
const Papers = lazy(() => import("./pages/Papers"));
const Gallery = lazy(() => import("./pages/Gallery"));
const Analytics = lazy(() => import("./pages/Analytics"));
const SignUp = lazy(() => import("./pages/SignUp").then((module) => ({ default: module.SignUp })));
const LogIn = lazy(() => import("./pages/LogIn").then((module) => ({ default: module.LogIn })));
const Onboarding = lazy(() => import("./pages/Onboarding").then((module) => ({ default: module.Onboarding })));
const Processing = lazy(() => import("./pages/Processing").then((module) => ({ default: module.Processing })));
const OAuthConsent = lazy(() => import("./pages/OAuthConsent").then((module) => ({ default: module.OAuthConsent })));

function RouteFallback() {
  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-6">
      <div className="text-sm text-muted-foreground">Loading workspace...</div>
    </div>
  );
}

function LegacyPapersRedirect() {
  const location = useLocation();

  return <Navigate to={`${APP_PAPERS_ROUTE}${location.search}${location.hash}`} replace />;
}

function LegacyPaperRedirect() {
  const { id } = useParams();
  const location = useLocation();

  return <Navigate to={`${paperRoute(id ?? '')}${location.search}${location.hash}`} replace />;
}

// Theme initializer component
function ThemeInitializer() {
  useEffect(() => {
    // Initialize theme from localStorage on app mount
    const storedTheme = normalizeStoredTheme(localStorage.getItem("theme"));
    const theme = storedTheme ?? getPreferredTheme();

    applyTheme(theme, { persist: false });
  }, []);

  return null;
}

const App = () => (
  <>
    <ThemeInitializer />
    <AuthProvider>
      <SubscriptionProvider>
        <TooltipProvider>
          <Toaster />
          <BrowserRouter>
            <BillingModalProvider>
              <Suspense fallback={<RouteFallback />}>
                <Routes>
                  <Route path="/" element={<Landing />} />
                  <Route path="/read/:publicationId" element={<PublishedPaper />} />
                  <Route path="/privacy" element={<PrivacyPolicy />} />
                  <Route path="/terms" element={<TermsOfService />} />
                  <Route path="/app" element={<Navigate to={APP_PAPERS_ROUTE} replace />} />
                  <Route path="/app/papers" element={
                    <ProtectedRoute>
                      <RequireOnboarding>
                        <Papers />
                      </RequireOnboarding>
                    </ProtectedRoute>
                  } />
                  <Route path="/app/gallery" element={
                    <ProtectedRoute>
                      <RequireOnboarding>
                        <Gallery />
                      </RequireOnboarding>
                    </ProtectedRoute>
                  } />
                  <Route path="/app/analytics" element={
                    <ProtectedRoute>
                      <RequireOnboarding>
                        <Analytics />
                      </RequireOnboarding>
                    </ProtectedRoute>
                  } />
                  <Route path="/app/papers/:id" element={
                    <ProtectedRoute>
                      <RequireOnboarding>
                        <Paper />
                      </RequireOnboarding>
                    </ProtectedRoute>
                  } />
                  <Route path="/papers" element={<LegacyPapersRedirect />} />
                  <Route path="/papers/:id" element={<LegacyPaperRedirect />} />
                  <Route path="/signup" element={<SignUp />} />
                  <Route path="/login" element={<LogIn />} />
                  <Route path="/onboarding" element={
                    <ProtectedRoute requireSubscription={false}>
                      <RedirectOnboardedUser>
                        <Onboarding />
                      </RedirectOnboardedUser>
                    </ProtectedRoute>
                  } />
                  <Route path="/oauth/consent" element={<OAuthConsent />} />
                  <Route path="/processing" element={
                    <ProtectedRoute requireSubscription={false}>
                      <Processing />
                    </ProtectedRoute>
                  } />
                  {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
                  <Route path="*" element={<NotFound />} />
                </Routes>
              </Suspense>
            </BillingModalProvider>
          </BrowserRouter>
        </TooltipProvider>
      </SubscriptionProvider>
    </AuthProvider>
  </>
);

export default App;
